import type { ShippingClient, TypedClient } from '@platform/openapi';
import type { GeoNames } from '../geography/useGeoAreas.ts';
import { useActiveLocations } from '../inventory/useStockMatrix.ts';
import { usePageContext } from '../usePageContext.ts';
import { companyNameOf, useCompanies } from '../companies.ts';
import { acceptsParcelChanges, carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { CancelOrderDialog } from './CancelOrderDialog.tsx';
import { DispatchOrderDialog } from './DispatchOrderDialog.tsx';
import { LabelDownload } from './LabelDownload.tsx';
import { OrderSheet } from './OrderSheet.tsx';
import {
  AWAITING_CARRIER,
  isAtCarrierBeforePickup,
  CARRIER_FOLLOW_INTERVAL_MS,
  type OrderDetail,
} from './orders.ts';
import { RefreshTracking } from './RefreshTracking.tsx';
import { useListFollowsStatus } from './useListFollowsStatus.ts';
import { useAvailableOrderActions } from './useAvailableOrderActions.ts';
import { useOrderDetail } from './useOrderDetail.ts';
import type { OrderMoves } from './useOrderMoves.ts';

export type OrderPanelsProps = {
  api: TypedClient;
  shipping: ShippingClient;
  openOrderId: string | null;
  geoNames: GeoNames;
  moves: OrderMoves;
  onOpenOrder: (orderId: string | null) => void;
  onOpenSettings: () => void;
};

const followWhileAwaitingCarrier = (detail: OrderDetail | undefined): number | false =>
  detail?.order.status === AWAITING_CARRIER ? CARRIER_FOLLOW_INTERVAL_MS : false;

export const OrderPanels = ({
  api,
  shipping,
  openOrderId,
  geoNames,
  moves,
  onOpenOrder,
  onOpenSettings,
}: OrderPanelsProps) => {
  const { t, describeError } = usePageContext();
  const depots = useActiveLocations(api).data?.items ?? [];
  const companies = useCompanies(api);
  const { actions, canceling, dispatching, setCanceling, setDispatching } = moves;
  const carriers = useCarriers(shipping);
  const availableFor = useAvailableOrderActions(acceptsParcelChanges(carriers.data?.items));
  const carrierName = (code: string): string => carrierNameOf(carriers.data?.items, code);
  const { detail } = useOrderDetail(api, openOrderId, followWhileAwaitingCarrier);
  useListFollowsStatus(detail?.order);

  return (
    <>
      {detail === undefined || canceling !== null || dispatching !== null ? null : (
        <OrderSheet
          detail={detail}
          depotName={depots.find((depot) => depot.id === detail.order.locationId)?.name ?? '—'}
          companyName={
            companies.subsidiaries.length === 0 ? null : companyNameOf(companies, detail.order.subsidiaryId)
          }
          geoNames={geoNames}
          carrierName={carrierName}
          available={availableFor(detail.order)}
          labelAction={
            detail.order.dispatch.shipmentId === null ? null : (
              <>
                <LabelDownload
                  shipping={shipping}
                  shipmentId={detail.order.dispatch.shipmentId}
                  onOpenSettings={onOpenSettings}
                />
                <RefreshTracking shipping={shipping} shipmentId={detail.order.dispatch.shipmentId} />
              </>
            )
          }
          busy={actions.isPending}
          onAction={moves.run}
          onClose={() => {
            onOpenOrder(null);
          }}
        />
      )}
      {canceling === null ? null : (
        <CancelOrderDialog
          title={t('orders.cancelTitle', { number: canceling.number })}
          notes={[
            t('orders.cancelMessage'),
            ...(isAtCarrierBeforePickup(canceling) && canceling.dispatch.carrierCode !== null
              ? [t('orders.cancelAtCarrier', { carrier: carrierName(canceling.dispatch.carrierCode) })]
              : []),
          ]}
          confirmLabel={t('orders.cancel')}
          keepLabel={t('orders.keepOrder')}
          busy={actions.isPending}
          errorMessage={actions.isError ? (describeError(actions.error) ?? null) : null}
          onKeep={() => {
            setCanceling(null);
          }}
          onCancel={(reason) => {
            actions.mutate(
              { kind: 'cancel', order: canceling, reason },
              { onSuccess: () => setCanceling(null) },
            );
          }}
        />
      )}
      {dispatching === null ? null : (
        <DispatchOrderDialog
          title={t('orders.dispatchTitle', { number: dispatching.number })}
          message={t('orders.dispatchMessage')}
          orders={[dispatching]}
          carriers={carriers.data?.items ?? []}
          loadingCarriers={carriers.isPending}
          busy={actions.isPending}
          errorMessage={actions.isError ? (describeError(actions.error) ?? null) : null}
          onOpenSettings={onOpenSettings}
          onClose={() => {
            setDispatching(null);
          }}
          onDispatch={(carrierCode, fees, idempotencyKey) => {
            actions.mutate(
              {
                kind: 'dispatch',
                order: dispatching,
                carrierCode,
                shippingFee: fees[dispatching.id] ?? '',
                idempotencyKey,
              },
              { onSuccess: () => setDispatching(null) },
            );
          }}
        />
      )}
    </>
  );
};
